// ============================================================================
// useSubDetailExtras (2026-07-10 REV 2) — the extra data the routed
// subcontractor DETAIL PAGE needs beyond useSubDetail: onboarding id +
// checklist (for the Payment-info missing chip), capability service labels
// (Active assignments ∪ projects), ended assignments, and project work split
// into recent/upcoming vs historic.
//
// Finance-flavored reads (partner_payments) are wrapped so an RLS denial for
// HR/AM degrades to an empty section rather than throwing the whole page.
// Flat-fetch + client-join, matching the house style.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { deriveCapabilities, type Capability } from '../capabilities';
import { catalogLabel } from '../../../shared/service-roles';

export interface SubAssignmentRow {
  assignment_id: string;
  facility_id: string | null;
  facility_name: string;
  service_code: string | null;
  service_label: string | null;
  status: string | null;
}

export interface SubProjectWork {
  key: string;
  project_id: string | null;
  label: string;
  service_type_raw: string | null;
  amount: number | null;
  date: string | null; // completion ?? due ?? start
  status: string | null;
  recent: boolean; // within 60 days or upcoming
}

export interface SubDetailExtras {
  onboardingId: string | null;
  checklistJson: Record<string, unknown> | null;
  capabilities: Capability[];
  rawServiceTypes: string[];
  endedAssignments: SubAssignmentRow[];
  recentProjects: SubProjectWork[];
  historicProjects: SubProjectWork[];
  loading: boolean;
}

const ACTIVE_ASSIGNMENT = 'Active';
const RECENT_WINDOW_MS = 60 * 86_400_000;

export function useSubDetailExtras(providerId: string | undefined): SubDetailExtras & {
  refetch: () => void;
} {
  const [state, setState] = useState<SubDetailExtras>({
    onboardingId: null,
    checklistJson: null,
    capabilities: [],
    rawServiceTypes: [],
    endedAssignments: [],
    recentProjects: [],
    historicProjects: [],
    loading: true,
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!providerId) return;
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true }));

      // Onboarding record (checklist → payment-info chip; onboarding_id → edit).
      const onbRes = await supabase
        .from('partner_onboarding')
        .select('onboarding_id, checklist_json')
        .eq('provider_id', providerId)
        .maybeSingle();
      const onboardingId = (onbRes.data?.onboarding_id as string | null) ?? null;
      const checklistJson = (onbRes.data?.checklist_json as Record<string, unknown> | null) ?? null;

      // All assignments — Active feeds capability; ended feed the Historic tab.
      const asgRes = await supabase
        .from('service_assignments')
        .select('assignment_id, facility_id, assignment_status, service_code, service_catalog(display_name)')
        .eq('provider_id', providerId);
      // ⚠️ The RAW shape, with the catalogue still embedded. The mapped row
      // below carries the label instead — two different shapes, named as two.
      const asgRows = (asgRes.data ?? []) as unknown as {
        assignment_id: string;
        facility_id: string | null;
        assignment_status: string | null;
        service_catalog: { display_name: string | null }[] | { display_name: string | null } | null;
        service_code: string | null;
      }[];
      const facIds = [...new Set(asgRows.map((a) => a.facility_id).filter(Boolean) as string[])];
      const facNames: Record<string, string> = {};
      if (facIds.length) {
        const facRes = await supabase
          .from('facilities')
          .select('facility_id, facility_name')
          .in('facility_id', facIds);
        for (const f of (facRes.data ?? []) as { facility_id: string; facility_name: string | null }[]) {
          facNames[f.facility_id] = f.facility_name ?? f.facility_id;
        }
      }
      const serviceTypeSet = new Set<string>();
      const endedAssignments: SubAssignmentRow[] = [];
      for (const a of asgRows) {
        const label = catalogLabel(a.service_catalog);
        if (a.assignment_status === ACTIVE_ASSIGNMENT && label) serviceTypeSet.add(label);
        if (a.assignment_status !== ACTIVE_ASSIGNMENT) {
          endedAssignments.push({
            assignment_id: a.assignment_id,
            facility_id: a.facility_id,
            facility_name: a.facility_id ? facNames[a.facility_id] ?? a.facility_id : '—',
            service_code: a.service_code,
            service_label: label,
            status: a.assignment_status,
          });
        }
      }

      // Project work for this sub. TCSD-0380 (item #680): every service line pays its
      // own sub, so a project belongs on this page when ANY of its lines carries the
      // sub, one row per line. A billing-comms note with no line names its sub on
      // the record itself (Decision 2, 2026-10-02: the note stays as built), so those
      // come in too. The project's own subcontractor slot is NOT read for a project
      // that has lines.
      const lineRes = await supabase
        .from('project_services')
        .select(
          'service_line_id, project_id, line_role, service_code, sub_rate, service_catalog(display_name), scope_text, ' +
            'projects!inner(project_id, account_name, facility_name, stage, start_date, due_date, completion_date)',
        )
        .eq('assigned_partner_id', providerId);
      type LineRow = {
        service_line_id: string;
        project_id: string;
        line_role: string | null;
        service_code: string | null;
        sub_rate: number | null;
        scope_text: string | null;
        service_catalog: { display_name: string | null }[] | { display_name: string | null } | null;
        projects: ProjRow | ProjRow[] | null;
      };
      type ProjRow = {
        project_id: string;
        account_name: string | null;
        facility_name: string | null;
        stage: string | null;
        start_date: string | null;
        due_date: string | null;
        completion_date: string | null;
      };
      const lineRows = (lineRes.data ?? []) as unknown as LineRow[];

      const noteRes = await supabase
        .from('projects')
        .select('project_id, account_name, facility_name, stage, start_date, due_date, completion_date, pay_amount, project_services(service_line_id)')
        .eq('assigned_partner_id', providerId);
      const noteRows = ((noteRes.data ?? []) as unknown as (ProjRow & {
        pay_amount: number | null;
        project_services: { service_line_id: string }[] | null;
      })[]).filter((p) => (p.project_services ?? []).length === 0);

      // Which lines and projects have a payment record — the line's own, or one for
      // the whole project (the AP report is the record, Decision 1). Finance read: degrade.
      const paidLines = new Set<string>();
      const paidProjects = new Set<string>();
      try {
        const lineIds = lineRows.map((l) => l.service_line_id);
        const projIds = [...new Set([...lineRows.map((l) => l.project_id), ...noteRows.map((p) => p.project_id)])];
        if (lineIds.length) {
          const r = await supabase.from('partner_payments').select('reimburses_service_line_id').in('reimburses_service_line_id', lineIds);
          for (const x of (r.data ?? []) as { reimburses_service_line_id: string | null }[]) if (x.reimburses_service_line_id) paidLines.add(x.reimburses_service_line_id);
        }
        if (projIds.length) {
          const r = await supabase.from('partner_payments').select('reimburses_project_id').in('reimburses_project_id', projIds);
          for (const x of (r.data ?? []) as { reimburses_project_id: string | null }[]) if (x.reimburses_project_id) paidProjects.add(x.reimburses_project_id);
        }
      } catch {
        /* keep empty — RLS denial */
      }

      const cutoff = new Date(Date.now() - RECENT_WINDOW_MS).toISOString().slice(0, 10);
      const recentProjects: SubProjectWork[] = [];
      const historicProjects: SubProjectWork[] = [];
      const place = (key: string, p: ProjRow, what: string | null, amount: number | null, paid: boolean) => {
        const date = p.completion_date ?? p.due_date ?? p.start_date ?? null;
        const cancelled_ = (p.stage ?? '').toLowerCase().includes('cancel');
        const recent = !cancelled_ && (date == null || date >= cutoff);
        const work: SubProjectWork = {
          key,
          project_id: p.project_id,
          label: [p.facility_name ?? p.account_name, what].filter(Boolean).join(' · ') || p.project_id,
          service_type_raw: what,
          amount,
          date,
          // A $0 line (hours on a timecard, a rate nobody stated) has nothing to pay,
          // so it is neither "paid" nor "not paid".
          status: [p.stage, !amount ? null : paid ? 'paid' : 'not paid'].filter(Boolean).join(' · ') || null,
          recent,
        };
        (recent ? recentProjects : historicProjects).push(work);
      };
      for (const l of lineRows) {
        const p = Array.isArray(l.projects) ? l.projects[0] : l.projects;
        if (!p) continue;
        const isChargeback = l.line_role === 'chargeback';
        // "Other - describe it" is a prompt, not a name: the AM's own words are the name (TCSD-0062).
        const what = isChargeback
          ? 'Chargeback'
          : (l.service_code === 'OTHER' ? null : catalogLabel(l.service_catalog)) ?? l.scope_text;
        // "Other - describe it" names no service, so it is not a capability.
        if (!isChargeback && what && l.service_code !== 'OTHER') serviceTypeSet.add(what);
        place(l.service_line_id, p, what, l.sub_rate, paidLines.has(l.service_line_id) || paidProjects.has(p.project_id));
      }
      for (const p of noteRows) {
        place(p.project_id, p, null, p.pay_amount, paidProjects.has(p.project_id));
      }
      const byDateDesc = (a: SubProjectWork, b: SubProjectWork) => (b.date ?? '').localeCompare(a.date ?? '');
      recentProjects.sort(byDateDesc);
      historicProjects.sort(byDateDesc);

      if (cancelled) return;
      const rawServiceTypes = [...serviceTypeSet];
      setState({
        onboardingId,
        checklistJson,
        capabilities: deriveCapabilities(rawServiceTypes),
        rawServiceTypes,
        endedAssignments,
        recentProjects,
        historicProjects,
        loading: false,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { ...state, refetch };
}
